/**
 * @file useFilterApply.ts
 * @desc Hook for applying filters
 * @copyright All rights reserved @ Inarix
 */
import { GridLogicOperator, GridFilterItem } from "@mui/x-data-grid-pro";
import { FilterItemExtended } from "../types";
import { processApiFilter, processLocalFilter } from "../utils/filterUtils";

interface UseFilterApplyProps {
  filterItems: FilterItemExtended[];
  columns: any[];
  apiOptions: { [key: number]: any[] };
  comesFromDashboard?: boolean;
  onFilterChange: (newFilterModel: {
    items: GridFilterItem[];
    logicOperator: GridLogicOperator;
  }) => void;
  onApplyFilters: () => void;
  clearAllFilters: () => void;
}

export const useFilterApply = ({
  filterItems,
  columns,
  apiOptions,
  comesFromDashboard,
  onFilterChange,
  onApplyFilters,
  clearAllFilters,
}: UseFilterApplyProps) => {
  const handleApplyFilter = () => {
    const validItems = filterItems
      .filter((item) => item.field && item.operator)
      .map((item, index) => {
        const column = columns.find((col) => col.field === item.field);

        if (column?.filter === "API" && item.operator === "$in") {
          return processApiFilter(
            item,
            index,
            apiOptions,
            columns,
            comesFromDashboard,
          );
        }

        if (column?.filter === "API" && item.operator !== "$in") {
          return processApiFilter(item, index, apiOptions, columns);
        }

        if (column?.filter === "LOCAL" && item.operator === "$in") {
          return processLocalFilter(item, index, apiOptions, columns);
        }

        return item;
      });

    const newFilterModel = {
      items: validItems,
      logicOperator: GridLogicOperator.And,
    };

    onFilterChange(newFilterModel);
    const hasApiFilters = validItems.some((item) => item.filter === "API");
    if (hasApiFilters) {
      onApplyFilters();
    }
  };

  const handleClearAll = () => {
    clearAllFilters();

    const newFilterModel = {
      items: [],
      logicOperator: GridLogicOperator.And,
    };

    onFilterChange(newFilterModel);
    onApplyFilters();
  };

  return {
    handleApplyFilter,
    handleClearAll,
  };
};
